'use client'

/**
 * Purpose: Public contact form — name, email, subject, message submitted to submitContact.
 * Inputs: none (locale from next-intl; copy from the `contactUs` namespace).
 * Outputs: Success panel on completion; Zod field errors rendered with role="alert"
 * and linked to their inputs via aria-describedby/aria-invalid; global error is assertive.
 */

import { useState, useTransition } from 'react'
import { useTranslations, useLocale } from 'next-intl'
import { captureContactFormSubmitted } from '@lib/interaction-telemetry'
import { submitContact } from '@/actions/contact'
import HoverGradientButton from '@/components/buttons/HoverGradientButton/HoverGradientButton'

const inputClass =
  'w-full bg-transparent border border-orange rounded-full px-4 py-2 text-black placeholder-black/40 focus:outline-none focus:border-orange focus:ring-1 focus:ring-orange'

const textareaClass =
  'w-full bg-transparent border border-orange rounded-2xl px-4 py-3 text-black placeholder-black/40 focus:outline-none focus:border-orange focus:ring-1 focus:ring-orange resize-none'

type FieldErrors = Record<string, string[] | undefined>

export function ContactForm() {
  const locale = useLocale()
  const t = useTranslations('contactUs')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [globalError, setGlobalError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isPending, startTransition] = useTransition()

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFieldErrors({})
    setGlobalError('')

    const fd = new FormData()
    fd.set('name', name)
    fd.set('email', email)
    fd.set('subject', subject)
    fd.set('message', message)
    fd.set('_locale', locale)

    startTransition(async () => {
      const result = await submitContact(null, fd)
      if (!result) return
      if ('error' in result) {
        setGlobalError(result.error as string)
      } else if ('errors' in result) {
        setFieldErrors((result.errors as { fieldErrors?: FieldErrors })?.fieldErrors ?? {})
      } else if ('success' in result) {
        captureContactFormSubmitted()
        setSuccess(true)
      }
    })
  }

  if (success) {
    return (
      <div id="contact-form-success" role="status" className="text-center py-16">
        <h2 id="contact-form-success-title" className="mb-4">{t('successTitle')}</h2>
        <p id="contact-form-success-body" className="text-black">{t('successBody')}</p>
      </div>
    )
  }

  return (
    <form id="contact-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
      {/* Each field: label ↔ input by id; Zod error ↔ input by aria-describedby. */}
      <div id="contact-name-field">
        <label htmlFor="contact-name" className="block text-black mb-1">{t('name')} *</label>
        <input
          id="contact-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('namePlaceholder')}
          className={inputClass}
          required
          aria-invalid={fieldErrors.name ? true : undefined}
          aria-describedby={fieldErrors.name ? 'contact-name-error' : undefined}
        />
        {fieldErrors.name && (
          <p id="contact-name-error" role="alert" className="text-red-600 text-sm mt-1">{fieldErrors.name[0]}</p>
        )}
      </div>

      <div id="contact-email-field">
        <label htmlFor="contact-email" className="block text-black mb-1">{t('email')} *</label>
        <input
          id="contact-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('emailPlaceholder')}
          className={inputClass}
          required
          aria-invalid={fieldErrors.email ? true : undefined}
          aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
        />
        {fieldErrors.email && (
          <p id="contact-email-error" role="alert" className="text-red-600 text-sm mt-1">{fieldErrors.email[0]}</p>
        )}
      </div>

      {/* Subject and message follow the same association pattern. */}
      <div id="contact-subject-field">
        <label htmlFor="contact-subject" className="block text-black mb-1">{t('subject')} *</label>
        <input
          id="contact-subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={t('subjectPlaceholder')}
          className={inputClass}
          required
          aria-invalid={fieldErrors.subject ? true : undefined}
          aria-describedby={fieldErrors.subject ? 'contact-subject-error' : undefined}
        />
        {fieldErrors.subject && (
          <p id="contact-subject-error" role="alert" className="text-red-600 text-sm mt-1">{fieldErrors.subject[0]}</p>
        )}
      </div>

      <div id="contact-message-field">
        <label htmlFor="contact-message" className="block text-black mb-1">{t('message')} *</label>
        <textarea
          id="contact-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('messagePlaceholder')}
          rows={5}
          className={textareaClass}
          required
          aria-invalid={fieldErrors.message ? true : undefined}
          aria-describedby={fieldErrors.message ? 'contact-message-error' : undefined}
        />
        {fieldErrors.message && (
          <p id="contact-message-error" role="alert" className="text-red-600 text-sm mt-1">{fieldErrors.message[0]}</p>
        )}
      </div>

      {/* Form-level failure (rate limit, send error) — assertive so it interrupts. */}
      {globalError && (
        <p id="contact-form-error" role="alert" aria-live="assertive" className="text-red-600 text-sm">
          {globalError}
        </p>
      )}

      <HoverGradientButton id="contact-form-submit" type="submit" disabled={isPending}>
        {isPending ? t('sending') : t('sendMessage')}
      </HoverGradientButton>
    </form>
  )
}
